import { useEffect, useState } from 'react';

const NON_TEXT_INPUTS = ['button', 'checkbox', 'color', 'file', 'hidden', 'image', 'radio', 'range', 'reset', 'submit'];

/** Whether `el` takes typed text (so a phone keyboard is up while it has focus). */
export function isTextEntry(el: Element | null): boolean {
  if (!el) return false;
  if (el instanceof HTMLTextAreaElement) return !el.readOnly;
  if (el instanceof HTMLInputElement) return !el.readOnly && !NON_TEXT_INPUTS.includes(el.type);
  return (el as HTMLElement).isContentEditable === true;
}

/**
 * True while a text field has focus, i.e. while a phone shows its keyboard.
 * The bottom tabs step aside then, as native apps do, instead of riding on top
 * of the keyboard and eating half the space left for the composer.
 */
export default function useKeyboardOpen(enabled: boolean): boolean {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setOpen(false);
      return undefined;
    }
    let timer: number | undefined;
    // Read the focus on the next tick: moving between two fields fires focusout
    // before focusin, and the bar must not flicker in between.
    const check = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setOpen(isTextEntry(document.activeElement)), 60);
    };
    window.addEventListener('focusin', check);
    window.addEventListener('focusout', check);
    check();
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('focusin', check);
      window.removeEventListener('focusout', check);
    };
  }, [enabled]);

  return open;
}
